import type {
  Citation,
  DynamicHintsResponse,
  UnifiedSearchApiResponse,
  Session,
  Store,
  StoreSettings,
  SuggestedCTA,
} from './types';
import { api, createApiHeaders } from '../utils/api';
import { makeAbortableRequest } from './request-handler.service';
import { getEnv } from '../../utils/env';

export type BaseStreamEvent =
  | { type: 'text'; chunk: string }
  | {
      type: 'metadata';
      citations: Citation[];
      suggestedCTA: SuggestedCTA | null;
      nonDefinedCTA?: { label: string; url: string } | null;
      confidence: number;
    };

export type TextStreamEvent =
  | BaseStreamEvent
  | { type: 'response'; data: UnifiedSearchApiResponse };

export const getSession = async ({
  storeId,
  source,
}: {
  storeId: string;
  source?: 'playground' | 'live';
}): Promise<Session> => {
  return api
    .post(`stores/${storeId}/chat/session`, {
      timeout: 30000,
      credentials: 'include',
      json: {
        storeId: storeId,
        ...(source && { source }),
      },
    })
    .json();
};

export const getCurrentStore = async (host: string): Promise<Store> => {
  return api
    .post(`organizations/stores`, {
      timeout: 30000,
      credentials: 'include',
      json: { domain: host },
    })
    .json<Store>();
};

export const getStoreByStoreId = async (storeId: string): Promise<Store> => {
  return api
    .get(`organizations/stores/${storeId}`, {
      timeout: 30000,
      credentials: 'include',
    })
    .json<Store>();
};

export const getProactiveEngagement = async ({
  productIdentifier,
  session,
  store,
}: {
  productIdentifier: string;
  session?: Session;
  store: Store;
}) => {
  const headers = createApiHeaders(session);

  return api
    .post(`stores/${store.id}/chat/proactive-engagement`, {
      json: { productIdentifier },
      timeout: 30000,
      credentials: 'include',
      headers,
    })
    .json<UnifiedSearchApiResponse>();
};

export const getDynamicHints = async ({
  productIdentifier,
  session,
  store,
}: {
  productIdentifier: string;
  session?: Session;
  store: Store;
}) => {
  const headers = createApiHeaders(session);

  return api
    .post(`stores/${store.id}/chat/dynamic-hints`, {
      json: { productIdentifier },
      timeout: 30000,
      credentials: 'include',
      headers,
    })
    .json<DynamicHintsResponse>();
};

export const getPreviousSearchResults = async ({
  searchId,
  session,
  store,
}: {
  searchId: string;
  session?: Session;
  store: Store;
}) => {
  const headers = createApiHeaders(session);

  return api
    .get(`stores/${store.id}/chat/unified-search/${searchId}`, {
      timeout: 30000,
      credentials: 'include',
      headers,
    })
    .json<UnifiedSearchApiResponse>();
};

export const getSearchResultsByUrl = async ({
  url,
  session,
  store,
}: {
  url: string;
  session?: Session;
  store: Store;
}) => {
  const headers = createApiHeaders(session);

  return api
    .get(`stores/${store.id}/chat/search-results-by-url${url}`, {
      timeout: 30000,
      credentials: 'include',
      headers,
    })
    .json<UnifiedSearchApiResponse>();
};

export const getStoreSettings = async (
  storeId: string
): Promise<StoreSettings> => {
  return api
    .get(`store-settings/${storeId}`, {
      timeout: 30000,
      credentials: 'include',
    })
    .json<StoreSettings>();
};

export const updateStoreSettings = async (
  storeId: string,
  updateData: { settings?: Record<string, unknown> }
): Promise<StoreSettings> => {
  return api
    .patch(`store-settings/${storeId}`, {
      timeout: 30000,
      credentials: 'include',
      json: updateData,
    })
    .json<StoreSettings>();
};

export const getDesignatedCtas = async (
  storeId: string
): Promise<SuggestedCTA[]> => {
  return api
    .get(`stores/${storeId}/custom-actions/designated`, {
      timeout: 30000,
      credentials: 'include',
    })
    .json<SuggestedCTA[]>();
};

export const sendOnboardingMessage = async ({
  userMessage,
  session,
}: {
  userMessage: string;
  session: Session;
}): Promise<UnifiedSearchApiResponse> => {
  const headers = createApiHeaders(session);

  return makeAbortableRequest<UnifiedSearchApiResponse>(
    'sendOnboardingMessage',
    (signal) =>
      api
        .post(`chat/onboarding-message`, {
          timeout: 30000,
          json: {
            userMessage,
          },
          credentials: 'include',
          headers,
          signal,
        })
        .json<UnifiedSearchApiResponse>()
  );
};

export const getCurrentOnboardingState = async ({
  session,
  store,
}: {
  session: Session;
  store: Store;
}): Promise<UnifiedSearchApiResponse> => {
  const headers = createApiHeaders(session);

  return api
    .get(`stores/${store.id}/chat/onboarding-state`, {
      timeout: 30000,
      credentials: 'include',
      headers,
    })
    .json<UnifiedSearchApiResponse>();
};

export interface PlaygroundFeedbackResponseDto {
  success: boolean;
  customRuleId?: string;
  curatedResponseId?: string;
  errors?: string[];
  wasFirstFeedback?: boolean;
}

export const checkSessionHasMessages = async (
  storeId: string,
  session: Session
): Promise<boolean> => {
  const headers = createApiHeaders(session);
  const result = await api
    .get(`stores/${storeId}/chat/session/has-messages`, {
      credentials: 'include',
      headers,
    })
    .json<{ hasMessages: boolean }>();
  return result.hasMessages;
};

export const sendConversionEvent = async ({
  storeId,
  session,
  data,
}: {
  storeId: string;
  session: Session;
  data?: Record<string, unknown>;
}): Promise<void> => {
  const hasMessages = await checkSessionHasMessages(storeId, session);
  await createEvent({
    storeId,
    eventName: 'conversion',
    data: { superbarConversion: hasMessages, ...data },
    session,
  });
};

export const createEvent = async ({
  storeId,
  eventName,
  data,
  session,
}: {
  storeId: string;
  eventName: string;
  data?: Record<string, unknown>;
  session?: Session;
}): Promise<void> => {
  const headers = createApiHeaders(session);

  return api
    .post(`stores/${storeId}/chat/events`, {
      json: { eventName, data: data || {} },
      credentials: 'include',
      headers,
      timeout: 30000,
    })
    .json();
};

export const startVoiceStreamFetch = ({
  question,
  productId,
  session,
  store,
  signal,
}: {
  question: string;
  productId?: string;
  session?: Session;
  store: Store;
  signal?: AbortSignal;
}): Promise<Response> => {
  const headers = createApiHeaders(session);
  const baseUrl = getEnv('BASE_API_URL');

  return fetch(`${baseUrl}/api/stores/${store.id}/chat/voice-stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    body: JSON.stringify({
      question,
      ...(productId ? { productIdentifier: productId } : {}),
    }),
    credentials: 'include',
    signal,
  });
};

export const startTextStreamFetch = ({
  question,
  productId,
  session,
  store,
  signal,
  filterGroups = [],
  enabledFlags = [],
  parentSearchId,
  page = 1,
  pageSize = 24,
}: {
  question: string;
  productId?: string;
  session?: Session;
  store: Store;
  signal?: AbortSignal;
  filterGroups?: Array<{
    filters: Array<{
      filterName: string;
      type?: string;
      value: string;
      label: string;
    }>;
  }>;
  enabledFlags?: string[];
  parentSearchId?: string;
  page?: number;
  pageSize?: number;
}): Promise<Response> => {
  const headers = createApiHeaders(session);
  const baseUrl = getEnv('BASE_API_URL');

  const searchParams = new URLSearchParams({
    question,
    page: page.toString(),
    pageSize: pageSize.toString(),
    ...(productId ? { productIdentifier: productId } : {}),
  });

  return fetch(
    `${baseUrl}/api/stores/${store.id}/chat/stream?${searchParams}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      body: JSON.stringify({
        appliedFilters: filterGroups.flatMap((fg) => fg.filters),
        enabledFlags,
        parentSearchId,
      }),
      credentials: 'include',
      signal,
    }
  );
};

async function* parseSSEStream(
  response: Response,
  errorMessage: string
): AsyncGenerator<{ eventType: string; data: string }> {
  if (!response.ok) {
    throw new Error(`${errorMessage}: ${response.status}`);
  }

  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  let eventType = '';
  let eventData = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (line.startsWith('event: ')) {
        eventType = line.slice(7).trim();
      } else if (line.startsWith('data: ')) {
        eventData = line.slice(6);
      } else if (line === '' && eventType) {
        // Empty line signals end of event
        yield { eventType, data: eventData };

        if (eventType === 'done') return;

        eventType = '';
        eventData = '';
      }
    }
  }
}

function parseTextOrMetadataEvent(
  eventType: string,
  data: string
): BaseStreamEvent | null {
  try {
    const parsed = JSON.parse(data);
    if (eventType === 'text' && parsed.chunk) {
      return { type: 'text', chunk: parsed.chunk };
    }
    if (eventType === 'metadata') {
      return {
        type: 'metadata',
        citations: parsed.citations,
        suggestedCTA: parsed.suggestedCTA,
        nonDefinedCTA: parsed.nonDefinedCTA ?? null,
        confidence: parsed.confidence,
      };
    }
  } catch {
    /* skip malformed JSON */
  }
  return null;
}

export async function* consumeVoiceStream(
  response: Response
): AsyncGenerator<BaseStreamEvent> {
  for await (const { eventType, data } of parseSSEStream(
    response,
    'Voice stream failed'
  )) {
    const event = parseTextOrMetadataEvent(eventType, data);
    if (event) yield event;
  }
}

export async function* consumeTextStream(
  response: Response
): AsyncGenerator<TextStreamEvent> {
  for await (const { eventType, data } of parseSSEStream(
    response,
    'Text stream failed'
  )) {
    const event = parseTextOrMetadataEvent(eventType, data);

    if (event) {
      yield event;
      continue;
    }

    if (eventType === 'response') {
      try {
        yield { type: 'response', data: JSON.parse(data) };
      } catch {
        /* skip malformed */
      }
    } else if (eventType === 'error') {
      try {
        const parsed = JSON.parse(data);
        throw new Error(parsed.error || 'Stream error');
      } catch (e) {
        if (e instanceof Error) throw e;
        throw new Error('Stream error');
      }
    }
  }
}

export async function* streamVoiceSearch({
  question,
  productId,
  session,
  store,
}: {
  question: string;
  productId?: string;
  session?: Session;
  store: Store;
}): AsyncGenerator<BaseStreamEvent> {
  const response = await startVoiceStreamFetch({
    question,
    productId,
    session,
    store,
  });
  yield* consumeVoiceStream(response);
}

export const requestTtsAudio = async ({
  text,
  session,
  store,
}: {
  text: string;
  session?: Session;
  store: Store;
}): Promise<Response> => {
  const headers = createApiHeaders(session);
  const baseUrl = getEnv('BASE_API_URL');

  return fetch(`${baseUrl}/api/stores/${store.id}/chat/tts`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    body: JSON.stringify({ text }),
    credentials: 'include',
  });
};
